import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ScoringSupplier } from '../../../../types/advise';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { LoadError } from '../LoadError';
import { EngagementSupplierScoring, useEngagementEditor } from './EngagementEditorContext';
import { FunctionScoringTable } from './FunctionScoringTable';
import { PanelHeader } from './PanelHeader';
import { RequestSupplierScoringDialog } from './RequestSupplierScoringDialog';
import { SupplierScoreCard } from './SupplierScoreCard';
import { clientScoresByKey, ratingKey, ratingRowsByKey, ratingsByKey, ScoringView } from './SupplierScoring.calc';

const VIEW_DESCRIPTIONS: Record<ScoringView, string> = {
  client: 'Showing the average of client scores. Your own ratings still feed the ranking and the supplier cards.',
  mine: 'Showing your ratings, which feed the ranking. The purple tick on each slider marks the client average.'
};

/** Supplier Scoring: UPSTACK's 0–100 rating of each shortlisted supplier on each function, one supplier at a time. */
export const SupplierScoringPanel = ({ supplierScoring }: { supplierScoring: EngagementSupplierScoring }) => {
  const { engagement, canEdit } = useEngagementEditor();
  const { scoring, failed, refresh, rate } = supplierScoring;
  const [activeId, setActiveId] = useState<string | null>(null);
  // Kept as picked, so the panel stays open with its notice if a refresh drops the supplier from the shortlist.
  const [requesting, setRequesting] = useState<ScoringSupplier | null>(null);
  const ratings = useMemo(() => ratingsByKey(scoring?.ratings ?? []), [scoring?.ratings]);
  const ratingRows = useMemo(() => ratingRowsByKey(scoring?.ratings ?? []), [scoring?.ratings]);
  const clientScores = useMemo(() => clientScoresByKey(scoring?.client_scores ?? []), [scoring?.client_scores]);
  const [view, setView] = useState<ScoringView>('client');
  const [openDetails, setOpenDetails] = useState<Set<string>>(new Set());

  // The shortlist may have changed on Supplier Selection since the last load.
  useEffect(() => {
    refresh();
  }, [refresh]);

  const header = (
    <PanelHeader
      title="Supplier Scoring"
      description="Rate each function to help refine the recommendation."
    />
  );

  if (!scoring) {
    return (
      <Box className="flex flex-col gap-3">
        {header}
        {failed ? (
          <LoadError
            subject="the supplier scores"
            consequence="there is nothing to show yet"
            onRetry={refresh}
          />
        ) : (
          <ComponentSpinner sx={{ padding: '3rem' }} />
        )}
      </Box>
    );
  }

  const { categories, suppliers } = scoring;
  // The first card is active until another is picked, and again if the picked one leaves the shortlist.
  const active = suppliers.find((supplier) => supplier.id === activeId) ?? suppliers[0];
  const scoredFunctionIds = active
    ? categories.flatMap((category) =>
        category.functions.filter((fn) => clientScores.has(ratingKey(active.id, fn.id))).map((fn) => fn.id)
      )
    : [];
  const allOpen = scoredFunctionIds.length > 0 && scoredFunctionIds.every((id) => openDetails.has(id));

  const pickSupplier = (supplierId: string) => {
    setActiveId(supplierId);
    setOpenDetails(new Set());
  };

  // One row's details at a time; "Expand all details" opens every row with client scores.
  const toggleDetails = (functionId: string) =>
    setOpenDetails((open) => {
      if (!open.has(functionId)) return new Set([functionId]);
      const rest = new Set(open);
      rest.delete(functionId);
      return rest;
    });

  return (
    <Box className="flex flex-col gap-4">
      {header}
      {failed && (
        <LoadError
          subject="the supplier scores"
          consequence="what shows may be out of date"
          onRetry={refresh}
        />
      )}
      {active ? (
        <>
          <Box className="flex gap-3 overflow-x-auto pb-1">
            {suppliers.map((supplier) => (
              <SupplierScoreCard
                key={supplier.id}
                supplier={supplier}
                categories={categories}
                ratings={ratings}
                active={supplier.id === active.id}
                onSelect={() => pickSupplier(supplier.id)}
                onRequestScoring={canEdit ? () => setRequesting(supplier) : undefined}
              />
            ))}
          </Box>
          <Box className="flex flex-wrap items-end justify-between gap-3">
            <TextBlock
              size="sm"
              color="grey6">
              {VIEW_DESCRIPTIONS[view]}
            </TextBlock>
            <Box className="flex items-center gap-3">
              {scoredFunctionIds.length > 0 && (
                <TextButton onClick={() => setOpenDetails(allOpen ? new Set() : new Set(scoredFunctionIds))}>
                  {allOpen ? 'Collapse all details' : 'Expand all details'}
                </TextButton>
              )}
              <TextSpan
                size="xs"
                color="grey6">
                VIEW
              </TextSpan>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                aria-label="View"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
                onChange={(_, next: ScoringView | null) => next && setView(next)}>
                <ToggleButton value="client">Client average</ToggleButton>
                <ToggleButton value="mine">My rating</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <FunctionScoringTable
            categories={categories}
            supplierId={active.id}
            ratings={ratings}
            onRate={canEdit ? (functionId, rating, note) => rate(active.id, functionId, rating, note) : undefined}
            client={{ view, clientScores, ratingRows, openDetails, onToggleDetails: toggleDetails }}
          />
        </>
      ) : (
        <Alert severity="info">Shortlist suppliers on Supplier Selection to score them.</Alert>
      )}
      {requesting && (
        <RequestSupplierScoringDialog
          engagementId={engagement.id}
          engagementName={engagement.engagement_name}
          supplier={requesting}
          categories={categories}
          onClose={() => setRequesting(null)}
          onChanged={refresh}
        />
      )}
    </Box>
  );
};
